'use client';

import {
  AssetImage,
  Status,
  TimeAmounts,
  isEquityAsset,
} from '../../../../common';
import { css } from '@bts-web/utils-style-engine';
import { AssetType, AssetDetailsTranslations } from '../../../types';
import { AssetDetailsPrice } from './AssetDetailsPrice/AssetDetailsPrice';
import { StockAssetDetailsTranslations } from '../AssetDetailsStocks/AssetDetailsStocks';
import { useAssetDetailsDataContext } from '../../AssetDetailsDataContext/AssetDetailsDataContext.use-client';
import { useCallback, useMemo } from 'react';
import { useFeatureFlag } from '../../../../statsig/sdk/provider';
import { Locale } from '@bts-web/utils-lokalise';
import { IslamicLabel } from '../../../../common/components/IslamicBanking/IslamicLabel';
import { MarketStatusIcon } from './MarketStatusIcon/MarketStatusIcon';
import { useModalDrawerContext } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { MarketHoursDrawer } from './MarketHoursDrawer/MarketHoursDrawer';

export interface AssetDetailsHeaderProps {
  translations: AssetDetailsTranslations & StockAssetDetailsTranslations;
  locale: Locale;
  marketClosingAt?: Date;
  marketClosedUntil?: Date;
  lastOpenTime?: Date;
  lastCloseTime?: Date;
}

export const AssetDetailsHeader = ({
  translations,
  locale,
  marketClosingAt,
  marketClosedUntil,
  lastOpenTime,
  lastCloseTime,
}: AssetDetailsHeaderProps) => {
  const { assetData, priceChange, timeRange } = useAssetDetailsDataContext();

  const feature_hasRealStocksEnabled = useFeatureFlag(
    'feature_hasRealStocksEnabled',
  );

  const isLegacy =
    feature_hasRealStocksEnabled &&
    !isEquityAsset(assetData?.__typename ?? '') &&
    assetData?.__typename !== AssetType.METAL;

  const { openDrawer } = useModalDrawerContext();

  const handleMarketHoursClick = useCallback(() => {
    openDrawer(MarketHoursDrawer, {
      disableCloseButton: false,
      position: 'bottom',
      assetName: assetData?.name ?? '',
      marketClosingAt,
      marketClosedUntil,
      lastOpenTime,
      lastCloseTime,
      locale,
    });
  }, [
    assetData?.name,
    marketClosingAt,
    marketClosedUntil,
    lastOpenTime,
    lastCloseTime,
    locale,
    openDrawer,
  ]);

  const feature_showMultipleCurrencyAssetPrice = useFeatureFlag(
    'feature_showMultipleCurrencyAssetPrice',
  );

  const marketValueProps: React.ComponentProps<typeof AssetDetailsPrice> = {
    label: translations.marketValue,
    locale: locale,
    value: assetData?.price,
    priceVariation: priceChange,
  };

  const timestampTranslationMappings: {
    [K in TimeAmounts]: string;
  } = {
    oneDay: translations.in24Hours,
    maxYears: translations.inMaxYears,
    oneMonth: translations.inOneMonth,
    oneYear: translations.inOneYear,
    sevenDays: translations.inOneWeek,
    sixMonths: translations.inOneMonth,
  };

  const assetDetailsPriceTimestampText = useMemo(() => {
    if (!timeRange) return '';

    return (
      timestampTranslationMappings[
        timeRange as keyof typeof timestampTranslationMappings
      ] || ''
    );
  }, [timeRange]);

  if (!assetData) return null;

  return (
    <section
      data-testid="asset details header"
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'extra_small_2',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'row',
            gap: 'extra_small_2',
            alignItems: 'center',
          })}
        >
          <span
            className={css({
              fontSize: 'title.medium_medium',
              fontWeight: 'title.medium_medium',
              lineHeight: 1.15,
            })}
          >
            {assetData.name}
          </span>

          {!isEquityAsset(assetData.__typename) && (
            <span
              className={css({
                fontSize: 'body.medium',
                fontWeight: 'body.medium',
                lineHeight: 1.15,
              })}
            >
              {assetData.symbol}
            </span>
          )}

          {isEquityAsset(assetData.__typename) && (
            <MarketStatusIcon
              marketClosingAt={marketClosingAt}
              marketClosedUntil={marketClosedUntil}
              onClick={handleMarketHoursClick}
            />
          )}

          <IslamicLabel text={translations.islamicShariahCompliant} />

          {isLegacy && (
            <Status
              text={translations.legacy}
              size="small"
              visual="filledNeutralLight"
            />
          )}
        </div>

        {assetData?.price && (
          <AssetDetailsPrice
            {...marketValueProps}
            hideLabel
            data-testid="marketValue"
            bolderTitle
            timestampText={assetDetailsPriceTimestampText}
            additionalPriceValues={assetData.prices || []}
            showMultipleCurrencyAssetPrice={
              feature_showMultipleCurrencyAssetPrice
            }
          />
        )}
      </div>

      <AssetImage
        testId="asset-details-header-logo"
        height={48}
        width={48}
        alt={assetData.name || ''}
        src={assetData.logoUrl || ''}
      />
    </section>
  );
};
